/**
 * User-Friendly Error Messages
 * Converts technical errors into plain English that users can understand
 */

// Map of technical errors to user-friendly messages
const errorMessageMap = {
  // Network errors
  'Network Error': 'Unable to connect to the server. Please check your internet connection and try again.',
  'Failed to fetch': 'Unable to reach the server. Please check your internet connection.',
  'timeout': 'The request took too long. Please try again.',
  
  // Response/JSON parsing errors
  'body stream already read': 'Connection issue. Please try again.',
  'body is disturbed or locked': 'Connection issue. Please try again.',
  'disturbed or locked': 'Connection issue. Please try again.',
  'Failed to execute': 'Something went wrong. Please try again.',
  'Unexpected token': 'Server error. Please try again.',
  'JSON.parse': 'Server error. Please try again.',
  
  // Authentication errors
  'Not authenticated': 'Your session has expired. Please sign in again.',
  'Unauthorized': 'Your session has expired. Please sign in again.',
  '401': 'Invalid email or password. Please try again.',
  'Invalid credentials': 'Invalid email or password. Please try again.',
  'Invalid email or password': 'Invalid email or password. Please try again.',
  'User not found': 'No account found with this email address.',
  
  // Validation errors
  'required': 'Please fill in all required fields.',
  'Invalid email': 'Please enter a valid email address.',
  'Password too short': 'Your password must be at least 8 characters long.',
  'Passwords do not match': 'The passwords you entered do not match.',
  
  // Resource errors
  'Not found': 'The requested information could not be found.',
  '404': 'The requested information could not be found.',
  'Already exists': 'This item already exists. Please try a different name.',
  
  // Server errors
  'Internal Server Error': 'Something went wrong on our end. Please try again in a few moments.',
  '500': 'Something went wrong on our end. Please try again in a few moments.',
  '502': 'Our server is temporarily unavailable. Please try again in a few moments.',
  '503': 'Our service is temporarily unavailable. Please try again in a few moments.',
  
  // Payment errors
  'Payment failed': 'Your payment could not be processed. Please check your payment details and try again.',
  'Card declined': 'Your card was declined. Please try a different payment method.',
  'Insufficient funds': 'Your card does not have sufficient funds. Please try a different payment method.',
  
  // File upload errors
  'File too large': 'The file you selected is too large. Please choose a smaller file.',
  'Invalid file type': 'This file type is not supported. Please choose a different file.',
  'Upload failed': 'Failed to upload the file. Please try again.',
  
  // Credits/limits
  'credits': 'You have run out of credits. Please upgrade your plan to continue.',
  'rate limit': 'You have made too many requests. Please wait a moment and try again.',
  'quota exceeded': 'You have reached your usage limit. Please upgrade your plan.',
};

/**
 * Convert technical error message to user-friendly message
 * @param {Error|string} error - The error object or message
 * @param {string} defaultMessage - Default fallback message
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyError = (error, defaultMessage = 'Something went wrong. Please try again.') => {
  // If error is null or undefined
  if (!error) return defaultMessage;
  
  // Get the error message
  let errorMessage = '';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error.response) {
    // Axios error with response
    errorMessage = error.response.data?.detail || 
                   error.response.data?.message || 
                   error.response.statusText || 
                   error.message || '';
  } else if (error.message) {
    // Standard error object
    errorMessage = error.message;
  } else {
    errorMessage = String(error);
  }
  
  // Check if error message contains any known technical terms
  const lowerError = errorMessage.toLowerCase();
  
  for (const [technical, friendly] of Object.entries(errorMessageMap)) {
    if (lowerError.includes(technical.toLowerCase())) {
      return friendly;
    }
  }
  
  // Check for HTTP status codes
  if (error.response?.status) {
    const statusCode = String(error.response.status);
    if (errorMessageMap[statusCode]) {
      return errorMessageMap[statusCode];
    }
  }
  
  // If the error message is already user-friendly (no technical jargon), return it
  const technicalTerms = ['undefined', 'null', 'function', 'object', 'exception', 'stack', 'trace'];
  const hasTechnicalTerm = technicalTerms.some(term => lowerError.includes(term));
  
  if (!hasTechnicalTerm && errorMessage.length < 100 && errorMessage.length > 10) {
    return errorMessage;
  }
  
  // Return default message for unknown errors
  return defaultMessage;
};

/**
 * Get context-specific error messages for different features
 */
export const contextualErrors = {
  health: {
    addMedicine: 'Unable to add medicine. Please check your information and try again.',
    logFood: 'Unable to log your meal. Please try again.',
    analyzeFood: 'Unable to analyze the image. Please try with a clearer photo.',
    logWorkout: 'Unable to save your workout. Please try again.',
    logWater: 'Unable to log water intake. Please try again.',
    getMedicalAdvice: 'Unable to get health advice right now. Please try again later.',
  },
  work: {
    createTask: 'Unable to create the task. Please try again.',
    updateTask: 'Unable to update the task. Please try again.',
    deleteTask: 'Unable to delete the task. Please try again.',
    analyzeDocument: 'Unable to analyze the document. Please try again.',
    askQuestion: 'Unable to get an answer right now. Please try again.',
  },
  life: {
    addBill: 'Unable to add the bill. Please check your information and try again.',
    addAppointment: 'Unable to add the appointment. Please try again.',
    addExpense: 'Unable to log the expense. Please try again.',
    updateBill: 'Unable to update the bill. Please try again.',
  },
  chat: {
    sendMessage: 'Unable to send your message. Please try again.',
    loadHistory: 'Unable to load chat history. Please refresh the page.',
  },
  auth: {
    login: 'Unable to sign in. Please check your email and password.',
    signup: 'Unable to create your account. Please try again.',
    updateProfile: 'Unable to update your profile. Please try again.',
  },
  payment: {
    checkout: 'Unable to process payment. Please try again or contact support.',
    subscription: 'Unable to update subscription. Please try again.',
  },
};

/**
 * Get user-friendly error with context
 * @param {Error|string} error - The error
 * @param {string} context - Context like 'health.addMedicine'
 * @returns {string} User-friendly error message
 */
export const getContextualError = (error, context) => {
  // Get the contextual default message
  const contextParts = context.split('.');
  const defaultMessage = contextParts.length === 2 
    ? contextualErrors[contextParts[0]]?.[contextParts[1]] || 'Something went wrong. Please try again.'
    : 'Something went wrong. Please try again.';
  
  return getUserFriendlyError(error, defaultMessage);
};

export default getUserFriendlyError;
